import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  Badge,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { Eye, EyeOff } from "lucide-react";
import { MdShield } from "react-icons/md";
import { Link as RouterLink, Navigate } from "react-router-dom";
import {
  verifyAdminPassword,
  isAdminSessionValid,
  isAllowedAdminUser,
  getLoggedInUserId,
  setAdminSession,
  clearAdminSession,
} from "../../config/admin";
import payinapoIcon from "../../assets/icon.png";

function AdminAccessDenied() {
  const pageBg = useColorModeValue(
    "linear-gradient(160deg, #f1f5f9 0%, #e2e8f0 45%, #f8fafc 100%)",
    "linear-gradient(160deg, #0a0a0b 0%, #121214 50%, #0a0a0b 100%)"
  );
  const cardBg = useColorModeValue("rgba(255,255,255,0.94)", "rgba(18,18,20,0.92)");
  const border = useColorModeValue("gray.200", "whiteAlpha.150");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const titleColor = useColorModeValue("gray.900", "white");
  const loggedIn = getLoggedInUserId();

  return (
    <Flex
      minH={{ base: "calc(100dvh - 56px)", md: "calc(100dvh - 64px)" }}
      w={{ base: "calc(100% + 24px)", sm: "calc(100% + 32px)", md: "calc(100% + 48px)", lg: "calc(100% + 64px)" }}
      align="center"
      justify="center"
      px={{ base: 4, md: 6 }}
      py={{ base: 4, md: 6 }}
      bg={pageBg}
      mx={{ base: -3, sm: -4, md: -6, lg: -8 }}
      my={{ base: -3, md: -8 }}
    >
      <Box
        w="full"
        maxW="380px"
        p={{ base: 5, md: 6 }}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={border}
        bg={cardBg}
        textAlign="center"
      >
        <Image src={payinapoIcon} alt="Payinapo" w={10} h={10} objectFit="contain" mx="auto" mb={3} />
        <Heading size="md" color={titleColor} mb={2}>
          Access denied
        </Heading>
        <Text fontSize="sm" color={muted} mb={4}>
          {loggedIn
            ? "This account is not authorized to use the admin console."
            : "Sign in with the authorized admin account to access this page."}
        </Text>
        <Button as={RouterLink} to={loggedIn ? "/shop" : "/"} size="md" colorScheme="brand" w="full">
          {loggedIn ? "Back to app" : "Go to sign in"}
        </Button>
      </Box>
    </Flex>
  );
}

function AdminLoginScreen({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const pageBg = useColorModeValue(
    "linear-gradient(160deg, #f1f5f9 0%, #e2e8f0 45%, #f8fafc 100%)",
    "linear-gradient(160deg, #0a0a0b 0%, #121214 50%, #0a0a0b 100%)"
  );
  const cardBg = useColorModeValue("rgba(255,255,255,0.94)", "rgba(18,18,20,0.92)");
  const border = useColorModeValue("gray.200", "whiteAlpha.150");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const titleColor = useColorModeValue("gray.900", "white");
  const inputBg = useColorModeValue("white", "rgba(255,255,255,0.06)");
  const inputBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const glow = useColorModeValue(
    "0 24px 60px rgba(15,23,42,0.12), 0 0 0 1px rgba(15,23,42,0.04)",
    "0 28px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06), 0 0 48px rgba(52,211,153,0.08)"
  );
  const badgeBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const errorBg = useColorModeValue("red.50", "rgba(239,68,68,0.12)");
  const errorBorder = useColorModeValue("red.200", "rgba(239,68,68,0.35)");

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    if (!isAllowedAdminUser()) {
      setError("This account is not authorized for admin access.");
      setSubmitting(false);
      return;
    }
    if (!verifyAdminPassword(password)) {
      setError("Incorrect admin password.");
      setSubmitting(false);
      return;
    }
    setAdminSession();
    onSuccess();
    setSubmitting(false);
  };

  return (
    <Flex
      minH={{ base: "calc(100dvh - 56px)", md: "calc(100dvh - 64px)" }}
      w={{ base: "calc(100% + 24px)", sm: "calc(100% + 32px)", md: "calc(100% + 48px)", lg: "calc(100% + 64px)" }}
      maxW="100vw"
      align="center"
      justify="center"
      px={{ base: 4, md: 6 }}
      py={{ base: 4, md: 6 }}
      bg={pageBg}
      mx={{ base: -3, sm: -4, md: -6, lg: -8 }}
      my={{ base: -3, md: -8 }}
    >
      <Box
        as="form"
        onSubmit={handleSubmit}
        w="full"
        maxW="380px"
        p={{ base: 5, md: 6 }}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={border}
        bg={cardBg}
        backdropFilter="blur(16px)"
        boxShadow={glow}
      >
        <VStack spacing={4} align="stretch">
          <VStack spacing={2} align="center" textAlign="center">
            <Image src={payinapoIcon} alt="Payinapo" w={10} h={10} objectFit="contain" mx="auto" />
            <Box>
              <Heading size="md" color={titleColor} letterSpacing="-0.02em" mb={0.5}>
                Admin sign in
              </Heading>
              <Text fontSize="sm" color={muted}>
                Enter your admin password to continue
              </Text>
            </Box>
            <Badge
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              alignSelf="center"
              gap={1}
              px={2.5}
              py={1}
              minH="26px"
              borderRadius="full"
              borderWidth="1px"
              borderColor={border}
              bg={badgeBg}
              color={muted}
              fontSize="xs"
              fontWeight="medium"
              textTransform="none"
              whiteSpace="nowrap"
            >
              <Icon as={MdShield} boxSize="12px" color="brand.500" flexShrink={0} aria-hidden />
              Password required
            </Badge>
          </VStack>

          <Box>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="xs" mb={1} fontWeight="semibold" color={titleColor}>
                Admin password
              </FormLabel>
              <InputGroup size="md">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  autoComplete="current-password"
                  pr="2.75rem"
                  bg={inputBg}
                  borderColor={inputBorder}
                  _focus={{ borderColor: "brand.400", boxShadow: "0 0 0 1px var(--chakra-colors-brand-400)" }}
                />
                <InputRightElement h="full">
                  <IconButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    icon={showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    onClick={() => setShowPassword((v) => !v)}
                    color={muted}
                    _hover={{ color: titleColor, bg: "transparent" }}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {error ? (
              <Box
                mb={3}
                px={2.5}
                py={1.5}
                borderRadius="lg"
                bg={errorBg}
                borderWidth="1px"
                borderColor={errorBorder}
              >
                <Text fontSize="xs" color="red.400">
                  {error}
                </Text>
              </Box>
            ) : null}

            <Button
              type="submit"
              w="full"
              size="md"
              isLoading={submitting}
              loadingText="Verifying…"
              bgGradient="linear(to-r, brand.300, brand.500)"
              color="gray.900"
              fontWeight="semibold"
              fontSize="sm"
              borderRadius="full"
              boxShadow="0 2px 12px rgba(52,211,153,0.22)"
              _hover={{
                bgGradient: "linear(to-r, brand.400, brand.600)",
                transform: "translateY(-1px)",
              }}
              _active={{ transform: "translateY(0)" }}
            >
              Enter console
            </Button>
          </Box>

          <Text fontSize="xs" color={muted} textAlign="center">
            <RouterLink to="/shop">
              <Text as="span" _hover={{ color: "brand.400" }}>
                ← Back to app
              </Text>
            </RouterLink>
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
}

export const AdminUnlockContext = createContext({ lockAdmin: () => {} });

export function useAdminUnlock() {
  return useContext(AdminUnlockContext);
}

/**
 * Route guard for /admin: authorized app user + admin password before children render.
 * Password session lasts 30 minutes (see ADMIN_SESSION_MS); revisit within that window skips re-entry.
 */
export default function AdminGate({ children }) {
  const loggedIn = getLoggedInUserId();
  const allowed = isAllowedAdminUser();
  const [unlocked, setUnlocked] = useState(isAdminSessionValid);

  const lockAdmin = useCallback(() => {
    clearAdminSession();
    setUnlocked(false);
  }, []);

  const syncSession = useCallback(() => {
    setUnlocked(isAdminSessionValid());
  }, []);

  useEffect(() => {
    if (!allowed) {
      clearAdminSession();
      setUnlocked(false);
    }
  }, [allowed]);

  useEffect(() => {
    syncSession();
    const interval = setInterval(syncSession, 30_000);
    window.addEventListener("focus", syncSession);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", syncSession);
    };
  }, [syncSession]);

  if (!loggedIn) {
    return <Navigate to="/" replace />;
  }

  if (!allowed) {
    return <AdminAccessDenied />;
  }

  if (!unlocked) {
    return <AdminLoginScreen onSuccess={() => setUnlocked(true)} />;
  }

  return (
    <AdminUnlockContext.Provider value={{ lockAdmin }}>
      {children}
    </AdminUnlockContext.Provider>
  );
}
